import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  ArrowLeft,
  Calendar,
  Edit2,
  Save,
  Star,
  BookOpen,
  Heart,
  HelpCircle,
  Sparkles,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import type { DailyJournal, JournalEntry } from "@/types";

/**
 * Journal detail page for viewing and editing a specific daily journal
 *
 * @returns Detailed journal view with edit capabilities
 */
export function JournalDetail() {
  const { date } = useParams<{ date: string }>();
  const { getToken } = useAuth();
  const [journal, setJournal] = useState<DailyJournal | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const [editedNextSteps, setEditedNextSteps] = useState("");

  useEffect(() => {
    const fetchJournal = async () => {
      if (!date) return;

      try {
        const token = await getToken();
        if (!token) return;

        const data = await api.getJournalByDate(token, date);
        setJournal(data);
        setEditedSummary(data?.overallSummary || "");
        setEditedNextSteps(data?.nextSteps || "");
      } catch (error) {
        console.error("Failed to fetch journal:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJournal();
  }, [getToken, date]);

  /**
   * Save edited journal content
   */
  const handleSave = async () => {
    if (!journal) return;

    try {
      const token = await getToken();
      if (!token) return;

      const updated = await api.updateJournal(token, journal.id, {
        overallSummary: editedSummary,
        nextSteps: editedNextSteps,
      });

      setJournal(updated);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update journal:", error);
    }
  };

  /**
   * Regenerate journal for this date
   */
  const handleRegenerate = async () => {
    if (!date || isRegenerating) return;

    const confirmed = window.confirm(
      "ジャーナルを再生成しますか？現在の内容は上書きされます。"
    );
    if (!confirmed) return;

    setIsRegenerating(true);
    try {
      const token = await getToken();
      if (!token) return;

      const regenerated = await api.generateDailyJournal(token, date, true);
      setJournal(regenerated);
      setEditedSummary(regenerated.overallSummary || "");
      setEditedNextSteps(regenerated.nextSteps || "");
    } catch (error) {
      console.error("Failed to regenerate journal:", error);
      alert("ジャーナルの再生成に失敗しました。");
    } finally {
      setIsRegenerating(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "study":
        return <BookOpen size={16} className="text-study" />;
      case "life":
        return <Heart size={16} className="text-life" />;
      default:
        return <HelpCircle size={16} className="text-other" />;
    }
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse-subtle text-primary-600">
          <Sparkles size={48} />
        </div>
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link
          to="/journals"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary-600 transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          Back to Journals
        </Link>

        <div className="bg-white rounded-2xl border p-12 text-center">
          <Calendar className="mx-auto text-slate-300 mb-4" size={64} />
          <h2 className="text-2xl font-display font-semibold text-slate-700">
            No journal for this date
          </h2>
          <p className="text-slate-500 mt-2">
            There is no journal entry for {date}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Back link */}
      <Link
        to="/journals"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-primary-600 transition-colors mb-6"
      >
        <ArrowLeft size={20} />
        Back to Journals
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 text-slate-500 mb-2">
              <Calendar size={18} />
              <span>{formatDate(journal.date)}</span>
            </div>
            <h1 className="text-2xl font-display font-bold text-primary-950">
              Daily Journal
            </h1>
            <div className="flex items-center gap-2 mt-2">
              {/* Importance stars */}
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className={
                    i < journal.importanceScore
                      ? "text-accent-500 fill-accent-500"
                      : "text-slate-300"
                  }
                />
              ))}
              <span className="text-sm text-slate-500 ml-2">
                Importance: {journal.importanceScore}/5
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Regenerate button */}
            <button
              onClick={handleRegenerate}
              disabled={isRegenerating || isEditing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRegenerating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  再生成中...
                </>
              ) : (
                <>
                  <RefreshCw size={18} />
                  再生成
                </>
              )}
            </button>

            {/* Edit/Save button */}
            <button
              onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
              disabled={isRegenerating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isEditing
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {isEditing ? (
                <>
                  <Save size={18} />
                  Save
                </>
              ) : (
                <>
                  <Edit2 size={18} />
                  Edit
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Overall summary */}
      <div className="bg-white rounded-2xl border p-6 mb-6">
        <h2 className="text-lg font-display font-semibold text-primary-950 mb-4">
          Summary
        </h2>
        {isEditing ? (
          <textarea
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            className="w-full h-32 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        ) : (
          <MarkdownRenderer
            content={journal.overallSummary}
            className="text-slate-700"
          />
        )}
      </div>

      {/* Mood reflection */}
      {journal.moodReflection && (
        <div className="bg-gradient-to-r from-life-light/50 to-transparent rounded-2xl border p-6 mb-6">
          <h2 className="text-lg font-display font-semibold text-primary-950 mb-4">
            Mood & Reflection
          </h2>
          <MarkdownRenderer
            content={journal.moodReflection}
            className="text-slate-700"
          />
        </div>
      )}

      {/* Journal entries by topic */}
      {journal.entries && journal.entries.length > 0 && (
        <div className="bg-white rounded-2xl border p-6 mb-6">
          <h2 className="text-lg font-display font-semibold text-primary-950 mb-4">
            Topics Covered
          </h2>
          <div className="space-y-4">
            {journal.entries.map((entry: JournalEntry) => (
              <div
                key={entry.id}
                className="p-4 bg-slate-50 rounded-xl border-l-4"
                style={{
                  borderColor:
                    entry.category === "study"
                      ? "#3b82f6"
                      : entry.category === "life"
                      ? "#ec4899"
                      : "#6366f1",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {getCategoryIcon(entry.category)}
                  <span className="font-medium text-primary-950">
                    {entry.topicName}
                  </span>
                </div>
                <div className="text-slate-600 text-sm">
                  <MarkdownRenderer content={entry.summary} />
                </div>
                {entry.keyPoints && (
                  <div className="text-slate-500 text-sm mt-2 italic">
                    Key points: <MarkdownRenderer content={entry.keyPoints} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next steps */}
      <div className="bg-gradient-to-r from-accent-50 to-transparent rounded-2xl border p-6">
        <h2 className="text-lg font-display font-semibold text-primary-950 mb-4">
          Next Steps
        </h2>
        {isEditing ? (
          <textarea
            value={editedNextSteps}
            onChange={(e) => setEditedNextSteps(e.target.value)}
            className="w-full h-24 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        ) : (
          <MarkdownRenderer
            content={journal.nextSteps || "No next steps defined"}
            className="text-slate-700"
          />
        )}
      </div>
    </div>
  );
}

